import type { RouteLocationRaw, Router } from "vue-router";
import {
  AtSign,
  Bell,
  Building2,
  Columns2,
  Download,
  Keyboard,
  Languages,
  Link,
  LogOut,
  Megaphone,
  RefreshCw,
  Settings,
  SunMoon,
  UserRound,
  Users,
} from "@lucide/vue";
import type {
  ConnectNavItem,
  ConnectSettingsSectionView,
  ConnectSettingsTopic,
} from "@blpde/connect";
import type { NxCommand } from "@blpde/nx-ui";
import { connectTileId } from "@blpde/connect/config";
import { useTilesEnabled } from "@/tiles/tileNav";
import { useTileWorkspaces } from "@/tiles/useTileWorkspaces";
import { apps, appAllowed, type AppEntry } from "@/integration";
import { appNav } from "@/integration/loader";
import { appTranslator } from "@/i18n/appI18n";
import {
  developmentSettingsModule,
  generalSettingsModule,
  listSectionKeywords,
  settingsListLocation,
  settingsTopicLocation,
  systemSettingsModule,
  type SettingsHubModule,
} from "@/settingsHub/hub";
import type { CommandContext } from "./commands";

/** What the shell's own commands need beyond routing. */
export interface ShellCommandContext extends CommandContext {
  /** Opens the palette with a query (e.g. „?“ for the shortcut overview). */
  openPalette: (query?: string) => void;
  /** The companies the account may switch to, loaded on demand. */
  companies: () => Promise<
    {
      tenantID: number;
      companyID: number;
      label: string;
      hint?: string;
      active: boolean;
    }[]
  >;
  switchCompany: (tenantID: number, companyID: number) => Promise<void>;
  setLocale: (locale: "de" | "en") => void;
  toggleTheme: () => void;
  signOut: () => Promise<void>;
  copy: (text: string) => void;
  reload: () => void;
  currentUrl: () => string;
}

function to(router: Router, target: RouteLocationRaw): string {
  try {
    return router.resolve(target).fullPath;
  } catch {
    return typeof target === "string" ? target : "";
  }
}

/**
 * The shell's own commands: switching company and language, the theme, the
 * pages every account has, the shortcut overview, the link of the current page
 * and signing out (I-2348).
 */
export function buildShellCommands(ctx: ShellCommandContext): NxCommand[] {
  const { router, t } = ctx;
  const group = t("connect.command.general");
  const page = (
    id: string,
    label: string,
    path: string,
    icon: NxCommand["icon"],
    keywords: string[],
  ): NxCommand => ({
    id,
    label,
    group,
    icon,
    keywords,
    category: "navigation",
    href: path,
    run: () => {
      void router.push(path);
    },
  });
  // Workspaces belong to the page around the tiles, a tile has none of its own.
  const workspaceCommands: NxCommand[] =
    connectTileId() || !useTilesEnabled().value
      ? []
      : [
          page(
            "connect.tiles.open",
            t("connect.tiles.openWorkspaces"),
            "/tiles",
            Columns2,
            [
              "kachel",
              "arbeitsfläche",
              "nebeneinander",
              "tile",
              "workspace",
              "split",
            ],
          ),
          {
            id: "connect.tiles.openBeside",
            label: t("connect.tiles.openBeside"),
            group,
            icon: Columns2,
            category: "action",
            keywords: ["kachel", "daneben", "nebeneinander", "tile", "split"],
            run: () => {
              const current = router.currentRoute.value;
              useTileWorkspaces().openBeside(
                current.path === "/tiles" ? null : current.fullPath,
              );
              void router.push("/tiles");
            },
          },
        ];
  return [
    ...workspaceCommands,
    {
      id: "connect.switchCompany",
      label: t("connect.command.switchCompany"),
      group,
      icon: Building2,
      category: "action",
      keywords: ["firma", "mandant", "company", "tenant", "wechseln", "switch"],
      run: () => {},
      children: async () =>
        (await ctx.companies()).map((c) => ({
          id: `connect.switchCompany.${c.tenantID}.${c.companyID}`,
          label: c.label,
          hint: c.active ? t("connect.command.activeCompany") : c.hint,
          icon: Building2,
          transient: true,
          run: () => ctx.switchCompany(c.tenantID, c.companyID),
        })),
    },
    {
      id: "connect.language",
      label: t("connect.command.language"),
      group,
      icon: Languages,
      category: "setting",
      keywords: [
        "sprache",
        "language",
        "deutsch",
        "englisch",
        "english",
        "german",
      ],
      run: () => {},
      children: () =>
        (["de", "en"] as const).map((locale) => ({
          id: `connect.language.${locale}`,
          label: t(`connect.command.locale.${locale}`),
          transient: true,
          run: () => ctx.setLocale(locale),
        })),
    },
    {
      id: "connect.theme",
      label: t("connect.command.theme"),
      group,
      icon: SunMoon,
      category: "setting",
      keywords: ["hell", "dunkel", "theme", "dark", "light", "nacht", "design"],
      run: () => ctx.toggleTheme(),
    },
    {
      id: "connect.shortcuts",
      label: t("connect.command.shortcuts"),
      group,
      icon: Keyboard,
      category: "help",
      bind: "mod+/",
      keywords: [
        "tastenkürzel",
        "kürzel",
        "shortcuts",
        "keyboard",
        "hilfe",
        "help",
        "präfixe",
      ],
      transient: true,
      run: () => ctx.openPalette("?"),
    },
    page(
      "connect.mentions",
      t("connect.command.mentions"),
      "/mentions",
      AtSign,
      ["erwähnungen", "mentions", "benachrichtigungen"],
    ),
    page(
      "connect.notifications",
      t("connect.command.notifications"),
      "/notifications",
      Bell,
      ["benachrichtigungen", "notifications", "glocke", "bell"],
    ),
    page("connect.people", t("connect.command.people"), "/people", Users, [
      "personen",
      "kollegen",
      "people",
      "team",
    ]),
    page(
      "connect.changelog",
      t("connect.command.changelog"),
      "/changelog",
      Megaphone,
      ["neu", "changelog", "neuigkeiten", "news", "release"],
    ),
    page(
      "connect.downloads",
      t("connect.command.downloads"),
      "/downloads",
      Download,
      ["downloads", "apps", "desktop", "installieren"],
    ),
    page(
      "connect.formicaId",
      t("connect.command.formicaId"),
      "/formica_id",
      UserRound,
      ["formica id", "anmeldung", "koppeln", "pairing"],
    ),
    {
      id: "connect.copyLink",
      label: t("connect.command.copyLink"),
      group,
      icon: Link,
      category: "action",
      keywords: ["link", "url", "kopieren", "copy", "teilen", "share"],
      transient: true,
      run: () => ctx.copy(ctx.currentUrl()),
    },
    {
      id: "connect.reload",
      label: t("connect.command.reload"),
      group,
      icon: RefreshCw,
      category: "action",
      keywords: ["neu laden", "reload", "aktualisieren", "refresh"],
      transient: true,
      run: () => ctx.reload(),
    },
    {
      id: "connect.signOut",
      label: t("connect.command.signOut"),
      group,
      icon: LogOut,
      category: "action",
      keywords: ["abmelden", "logout", "sign out", "ausloggen"],
      transient: true,
      run: () => ctx.signOut(),
    },
  ];
}

/** One command per settings topic, opening its page in the hub. */
export function buildSettingsCommands(
  modules: readonly SettingsHubModule[],
  ctx: CommandContext,
): NxCommand[] {
  const group = ctx.t("connect.command.settings");
  return modules.flatMap((module) => {
    const moduleLabel = ctx.t(module.labelKey);
    const tt = module.app ? appTranslator(module.app) : ctx.t;
    return module.topics.map((topic: ConnectSettingsTopic) => {
      const target = to(ctx.router, settingsTopicLocation(module.id, topic.id));
      return {
        id: `connect.settings.${module.id}.${topic.id}`,
        label: `${moduleLabel}: ${tt(topic.labelKey)}`,
        group,
        icon: module.icon ?? Settings,
        category: "setting",
        keywords: ["einstellungen", "settings", ...(topic.keywords ?? [])],
        href: target,
        run: () => {
          void ctx.router.push(target);
        },
      } satisfies NxCommand;
    });
  });
}

/**
 * One command per section of the one settings list (I-2510), opening the list
 * on it — „System: Darstellung", „Mail: Einstellungen" — found by every
 * setting the section holds. Connect's own sections are filed under
 * „System", an app's under the app; an app's one section named like the app
 * reads „Einstellungen", as in the hub.
 */
export function buildSettingsListCommands(
  sections: readonly ConnectSettingsSectionView[],
  ctx: CommandContext,
): NxCommand[] {
  const group = ctx.t("connect.command.settings");
  return sections.map((section) => {
    const app = section.app
      ? apps.find((entry) => entry.id === section.app)
      : undefined;
    const moduleLabel = app
      ? ctx.t(app.labelKey)
      : ctx.t("connect.settingsHub.system");
    const sectionLabel =
      section.label === moduleLabel
        ? ctx.t("connect.settingsHub.listItem")
        : section.label;
    const target = to(ctx.router, settingsListLocation(section.id));
    return {
      id: `connect.settings.list.${section.id}`,
      label: `${moduleLabel}: ${sectionLabel}`,
      group,
      icon: app?.icon ?? Settings,
      category: "setting",
      keywords: ["einstellungen", "settings", ...listSectionKeywords(section)],
      href: target,
      run: () => {
        void ctx.router.push(target);
      },
    } satisfies NxCommand;
  });
}

/** The settings commands of Connect's own hub entries (Allgemein, System, Development). */
export function buildShellSettingsCommands(ctx: CommandContext): NxCommand[] {
  return buildSettingsCommands(
    [generalSettingsModule, systemSettingsModule, developmentSettingsModule],
    ctx,
  );
}

function navText(app: AppEntry, text: string): string {
  const translated = appTranslator(app.id)(text);
  return translated && translated !== text ? translated : text;
}

/**
 * Every navbar entry of every app the account may use becomes a jump — „ERP:
 * Angebote“, „Admin: Konten“ — without anyone listing them by hand. Entries of
 * an app that is not loaded yet come from its static `nav`; once loaded, from
 * the module's own.
 */
export function buildNavCommands(
  ctx: CommandContext,
  existingLabels: ReadonlySet<string>,
): NxCommand[] {
  const out: NxCommand[] = [];
  const seen = new Set<string>(existingLabels);
  for (const app of apps) {
    if (ctx.can && !appAllowed(app, ctx.can)) continue;
    const spec = appNav(app);
    if (!spec) continue;
    const appLabel = ctx.t(app.labelKey);
    const walk = (
      items: readonly ConnectNavItem[] | undefined,
      trail: string[],
    ) => {
      for (const item of items ?? []) {
        if (item.disabled) continue;
        if (
          item.requiredPermissions &&
          ctx.can &&
          !ctx.can(item.requiredPermissions, item.permissionMode ?? "all")
        ) {
          continue;
        }
        const text = navText(app, item.text);
        if (item.to) {
          const target = to(ctx.router, item.to);
          const label = `${appLabel}: ${[...trail, text].join(" › ")}`;
          const key = label.toLowerCase();
          if (target && !seen.has(key)) {
            seen.add(key);
            out.push({
              id: `connect.nav.${app.id}.${item.id}`,
              label,
              group: appLabel,
              icon: item.icon ?? app.icon,
              app: app.id,
              category: "navigation",
              keywords: [app.id, text],
              href: target,
              run: () => {
                void ctx.router.push(target);
              },
            });
          }
        }
        walk(item.children, item.to ? trail : [...trail, text]);
      }
    };
    walk(spec.left, []);
    walk(spec.right, []);
  }
  return out;
}
